import { useEffect } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { identifyUser, resetAnalytics, track } from '../services/analytics';
import { userStore } from '../store/userStore';
import { setTokenProvider, requestTokenRefresh } from '../services/api';
import { setAppInsightsUser, clearAppInsightsUser } from '../services/appInsights';
import { config } from '../config';
import { trackAdsConversion } from '../services/googleAds';

const CLERK_JWT_TEMPLATE = config.clerk.jwtTemplate;
const getClerkTokenOptions = (forceRefresh = false) =>
  CLERK_JWT_TEMPLATE
    ? { template: CLERK_JWT_TEMPLATE, skipCache: forceRefresh }
    : { skipCache: forceRefresh };

/**
 * Connects Clerk authentication to custom user store.
 * This component handles the bridge between Clerk and our global state.
 */
export const ClerkUserSync: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isSignedIn, isLoaded, getToken } = useAuth();
  const { user } = useUser();

  useEffect(() => {
    if (!isLoaded || !isSignedIn) {
      setTokenProvider(null);
      clearAppInsightsUser();
      return;
    }

    const provider = async (options?: { forceRefresh?: boolean }) =>
      getToken(getClerkTokenOptions(Boolean(options?.forceRefresh)));
    setTokenProvider(provider);
    requestTokenRefresh();

    return () => {
      setTokenProvider(null);
    };
  }, [isLoaded, isSignedIn, getToken]);

  // Sync Clerk auth state to user store
  useEffect(() => {
    if (isLoaded) {
      if (isSignedIn && user) {
        userStore.setClerkAuth(user, isLoaded, isSignedIn);
        setAppInsightsUser(user.id);
        identifyUser(user.id, {
          email: user.primaryEmailAddress?.emailAddress,
          name: [user.firstName, user.lastName].filter(Boolean).join(' ') || undefined,
        });
        // Fire signup_completed exactly once for freshly created accounts.
        // Clerk redirects new signups to /auth/business-setup (business) or
        // /auth/individual-plans (individual); createdAt distinguishes a new
        // signup from a returning login.
        const createdAt = user.createdAt ? new Date(user.createdAt).getTime() : 0;
        const isFreshSignup = createdAt > 0 && Date.now() - createdAt < 5 * 60 * 1000;
        const signupTrackedKey = `ph_signup_tracked:${user.id}`;
        const pathname = window.location.pathname;
        if (isFreshSignup && !sessionStorage.getItem(signupTrackedKey)) {
          sessionStorage.setItem(signupTrackedKey, '1');
          track('signup_completed', {
            signup_type: pathname.startsWith('/auth/business') ? 'business' : 'individual',
          });
        }
        // Google Ads "individual sign-up" conversion: only for accounts created
        // through the individual sign-up flow (Clerk lands them on
        // /auth/individual-plans), never for firm sign-ups or invited users.
        // Deduped per user id in localStorage so returning visits to
        // /app/assistant, reloads and new tabs never re-fire it.
        if (isFreshSignup && pathname.startsWith('/auth/individual')) {
          trackAdsConversion('individual_signup', user.id);
        }
      } else {
        userStore.setClerkAuth(null, isLoaded, false);
        clearAppInsightsUser();
        resetAnalytics();
      }
    }
  }, [isLoaded, isSignedIn, user, getToken]);

  return <>{children}</>;
};